/**
 * <VerdictPill> — F044 §9.1. Renders a verdict word coloured ONLY by `verdict.colorIntent -> a
 * reserved semantic token`. A zero-denominator `Verdict` (`kind === 'State'`) can ONLY be rendered by
 * `<EmptyVerdict>` — this component refuses it (throws in dev, falls back to a neutral "Unknown" pill
 * in prod, matching the F044 §9.1 contract) so a page cannot accidentally paint a no-data state as a
 * pass/fail. Never colours an unset value ("--" / "Not Set") — it renders unset text with no colour.
 */
import type { FC } from 'react';
import { Text, makeStyles, tokens } from '@fluentui/react-components';
import type { Verdict } from '../../types';
import { colorIntentToken } from './vocabulary';

const UNSET_LABELS = new Set(['--', 'Not Set', 'Unset']);

const useStyles = makeStyles({
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    borderRadius: tokens.borderRadiusCircular,
    fontWeight: tokens.fontWeightSemibold,
  },
  unset: {
    display: 'inline-flex',
    alignItems: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

export interface VerdictPillProps {
  verdict: Verdict;
}

export const VerdictPill: FC<VerdictPillProps> = ({ verdict }) => {
  const styles = useStyles();

  if (verdict.kind === 'State') {
    if (import.meta.env.DEV) {
      throw new Error(
        `<VerdictPill> refuses a State verdict ("${verdict.label}") — render <EmptyVerdict> instead. ` +
          'A zero-denominator state may never be painted as a pass/fail pill (F044 §9.1).'
      );
    }
    return (
      <span className={styles.pill} style={{ backgroundColor: colorIntentToken('Neutral').background, color: colorIntentToken('Neutral').foreground }}>
        <Text weight="semibold">Unknown</Text>
      </span>
    );
  }

  if (UNSET_LABELS.has(verdict.label)) {
    return <span className={styles.unset}>{verdict.label}</span>;
  }

  const { foreground, background } = colorIntentToken(verdict.colorIntent);

  return (
    <span className={styles.pill} style={{ backgroundColor: background, color: foreground }} role="status">
      <Text weight="semibold">{verdict.label}</Text>
    </span>
  );
};
